/**
 * biome-ignore-all lint/style/useNamingConvention: external data
 * biome-ignore-all lint/nursery/noExcessiveLinesPerFunction: This is a function that contains an object with multiple functions so it will be long
 * biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: This function is complex due to handling multiple event types and state transitions
 */

import type { Client } from '@optimizely/optimizely-sdk';
import { CookieService } from '@/app_lib/CookieService';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import {
  getTrackedUserClassification,
  trackCurrentUserConversion,
  trackCurrentUserStateChange,
} from '@/app_lib/services/Gtm/utils';
import {
  buildTemplateIntersectionObserver,
  getExistingPianoUid,
  getLts,
  PROMO_CONTAINER_OPTIMIZELY_EVENTS,
  registerPasswordlessPianoUser,
} from '@/app_lib/services/Piano/pianoHelpers';
import type {
  CheckoutCustomEvent,
  CustomEvent,
  LoginDataProps,
  OfferParams,
  OfferTerms,
  Params,
  ResponseVariableEvent,
  ResponseVariables,
  TemplateParams,
  Term,
  TermDetailsProps,
} from '@/app_lib/types/accounts/piano/Piano';
import type { Tinypass } from '@/app_lib/types/accounts/piano/tinypass';
import type { DataLayerEventName, EventData } from '@/app_lib/types/Gtm';
import { isSubscribePage } from '@/app_lib/utils/clientUtils';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { getLocalStorageObject, updateLocalStorageObject } from '@/app_lib/utils/localStorageUtils';
import { geoIp } from '@/app_lib/utils/paymentApi/geoip';
import { findUserInPiano, passwordlessRegister } from '@/app_lib/utils/personalizationApi/user';
import { getSessionStorageObject, removeSessionStorageItem } from '@/app_lib/utils/sessionStorageUtils';
import { OPTIMIZELY_EVENT_VALUES } from '@/main/lib/providers/OptimizelyProvider/utils';

let reloadOnClose = false;
const SUBSCRIPTION_RESUME_RELOAD_DELAY_MS = 2000;
interface SessionData {
  checkoutStarted: boolean | undefined;
  currentTerm: Term | undefined;
  offerTermClicked: boolean;
  offerTerms: OfferTerms | undefined;
  termUnset: boolean;
}

const sessionData: SessionData = {
  checkoutStarted: undefined,
  currentTerm: undefined,
  offerTermClicked: false,
  offerTerms: undefined,
  termUnset: true,
};

const unsetTermProps = {
  currency: 'Currency unset',
  description: 'Description unset',
  name: 'Name unset',
  price: 'Price unset',
};

let productPayload: {
  displayMode: string;
  experienceActionId: string;
  experienceId: string;
  lts: string;
  offerId: string;
  templateVariantId: string;
  templateId: string;
  terms: string[] | undefined;
};

export const pianoHandlers = (
  tp: Tinypass,
  updateResponseVariables: (responseVariables: Partial<ResponseVariables>) => void,
  sendEventOnConsent: (eventData: EventData, eventType?: DataLayerEventName) => void,
  optimizely: Client,
) => ({
  checkoutClose: (event: CheckoutCustomEvent) => {
    const subscribePage = isSubscribePage();
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect');
    const redirect = () => {
      if (subscribePage) {
        window.location.href = redirectUrl || '/';
      } else {
        window.location.reload();
      }
    };

    switch (event.state) {
      case 'close':
        if (!reloadOnClose) {
          sendEventOnConsent({
            eventAction: 'form abandoned',
            eventCategory: 'paywall',
            eventLabel: 'user abandoned the form',
          });
        } else if (subscribePage) {
          window.location.href = redirectUrl || '/';
        } else {
          window.location.reload();
        }
        break;
      case 'checkoutCompleted':
        trackOptimizelyEvent(optimizely, 'net_new', {});
        if (subscribePage) {
          trackOptimizelyEvent(optimizely, 'nav_button_subcomplete', {});
        } else {
          trackOptimizelyEvent(optimizely, 'paywall_subcomplete', {});
        }
        redirect();
        break;
      case 'alreadyHasAccess': {
        redirect();
        break;
      }
      default:
        break;
    }
  },
  checkoutCustomEvent: (event: CheckoutCustomEvent) => {
    switch (event.eventName) {
      case 'termSelected':
        {
          const { termId } = event.params;
          if (termId) {
            let selectedProduct: Term;
            if (sessionData?.offerTerms?.[termId]) {
              selectedProduct = sessionData.offerTerms[termId];
              sessionData.termUnset = false;
            } else {
              sessionData.termUnset = true;
              selectedProduct = {
                ...unsetTermProps,
                id: termId,
              };
            }
            sessionData.offerTermClicked = true;
            sessionData.currentTerm = selectedProduct;
            const productAddedPayload = {
              ...productPayload,
              currency: selectedProduct.currency,
              logged_in: Boolean(tp.pianoId.getUser()),
              products: [selectedProduct],
              timestamp: Date.now(),
              value: selectedProduct.price,
            };

            sendEventOnConsent({
              eventAction: 'product added',
              eventCategory: 'paywall',
              eventLabel: 'product added',
              ...productAddedPayload,
            });
            trackOptimizelyEvent(optimizely, 'user_subscribed', {});
            // Track general subscription tracking when term is selected
            trackOptimizelyEvent(optimizely, 'subscription_tracking', {});
          }
        }
        break;
      case 'term-changed':
        {
          const { termId } = event.params;
          if (termId && termId !== 'no change') {
            let removedProduct: Term;
            if (sessionData?.offerTerms?.[termId]) {
              removedProduct = sessionData.offerTerms[termId];
            } else {
              removedProduct = {
                ...unsetTermProps,
                id: termId,
              };
            }
            const productRemovedPayload = {
              ...productPayload,
              currency: removedProduct.currency,
              logged_in: Boolean(tp.pianoId.getUser()),
              products: [removedProduct],
              timestamp: Date.now(),
              value: removedProduct.price,
            };
            sendEventOnConsent({
              eventAction: 'product removed',
              eventCategory: 'paywall',
              eventLabel: 'product removed from cart',
              ...productRemovedPayload,
            });
            trackOptimizelyEvent(optimizely, 'product_removed', {});
          }
        }
        break;
      case 'payment-method-selected':
        if (sessionData.currentTerm) {
          const checkoutStartedPayload = {
            ...productPayload,
            currency: sessionData.currentTerm.currency,
            logged_in: Boolean(tp.pianoId.getUser()),
            products: [sessionData.currentTerm],
            timestamp: Date.now(),
            value: sessionData.currentTerm.price,
          };
          sendEventOnConsent({
            eventAction: 'checkout started',
            eventCategory: 'paywall',
            eventLabel: 'checkout started',
            ...checkoutStartedPayload,
          });
        }
        break;
      default:
        break;
    }
  },
  checkoutSelectTerm: (termDetails: TermDetailsProps) => {
    if (sessionData.offerTermClicked) {
      sessionData.offerTermClicked = false;
    } else {
      const { termId } = termDetails;
      let selectedProduct: Term;
      if (sessionData.offerTerms?.[termId]) {
        selectedProduct = sessionData.offerTerms[termId];
        sessionData.termUnset = false;
      } else {
        sessionData.termUnset = true;
        selectedProduct = {
          ...unsetTermProps,
          id: termId,
        };
      }

      sessionData.currentTerm = selectedProduct;
      const productAddedPayload = {
        ...productPayload,
        currency: selectedProduct.currency,
        logged_in: Boolean(tp.pianoId.getUser()),
        products: [selectedProduct],
        timestamp: Date.now(),
        value: selectedProduct.price,
      };

      sendEventOnConsent({
        eventAction: 'product added',
        eventCategory: 'paywall',
        eventLabel: 'product added to cart',
        ...productAddedPayload,
      });

      if (!isSubscribePage()) {
        trackOptimizelyEvent(optimizely, 'paywall_cta_click', {});
      }
    }
  },
  customEvent: async (event: CustomEvent) => {
    let email = '';
    let iframeId = '';
    let country = '';
    let formName = '';
    let registrationConsent = 'false';
    let consent = false;
    let templateId = '';
    let displayMode: undefined | 'modal' | 'inline';
    if (typeof event.params?.email !== 'undefined' && event.params.email.length > 0) {
      email = event.params.email;
    }
    if (typeof event.params?.country !== 'undefined' && event.params.country.length > 0) {
      country = event.params.country;
    }
    if (typeof event.params?.registrationconsent !== 'undefined' && event.params.registrationconsent.length > 0) {
      registrationConsent = event.params.registrationconsent;
    }
    if (typeof event.params?.formname !== 'undefined' && event.params.formname.length > 0) {
      formName = event.params.formname;
    }
    if (typeof event.params?.consent !== 'undefined' && event.params.consent.length > 0) {
      consent = event.params.consent === 'true';
    }
    if (typeof event.params?.params !== 'undefined') {
      try {
        const params = JSON.parse(event.params.params) as Params;
        iframeId = params.iframeId;
        templateId = params.templateId;
        displayMode = params.displayMode as 'modal' | 'inline';
      } catch (_error) {
        // console.error(error);
      }
    }
    const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
    let iframeOrigin = '';
    if (iframe) {
      iframeOrigin = new URL(iframe.src).origin;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || '';
    const utmMedium = urlParams.get('utm_medium') || '';
    const utmCampaign = urlParams.get('utm_campaign') || '';

    switch (event.eventName) {
      case 'passwordlessClose':
        window.location.reload();
        break;
      case 'passwordlessRegister': {
        const findUser = await findUserInPiano(email);
        if (findUser) {
          iframe.contentWindow?.postMessage(
            {
              event: 'error message passwordless registration',
              info: 'User already exists!',
            },
            iframeOrigin,
          );
        } else {
          const registerResponse = await passwordlessRegister(email, registrationConsent)
            .then((response) => response)
            .catch((_error: Error) => {
              // console.error(error);
            });

          if (!registerResponse) {
            return;
          }

          iframe.contentWindow?.postMessage(
            {
              event: 'success message passwordless registration',
              info: 'User was registered successfully!',
            },
            iframeOrigin,
          );

          CookieService.set('__utp', registerResponse.accessToken, {
            expires: registerResponse.expiresIn,
          });

          const trackLogin = async () => {
            try {
              trackOptimizelyEvent(optimizely, 'user_signed_in', {});
              await geoIp(registerResponse.pianoUid, 'registration_wall', registerResponse.accessToken);
              sendEventOnConsent({
                eventAction: 'create account',
                eventCategory: 'account',
                eventLabel: 'account creation successful',
              });
              trackOptimizelyEvent(optimizely, 'user_signed_up', { value: OPTIMIZELY_EVENT_VALUES.signUpValue });
            } catch (_error) {
              // console.error(error);
            }
          };

          await trackLogin();
          const previousClassification = getTrackedUserClassification();
          trackCurrentUserConversion('registration', previousClassification);
          trackCurrentUserStateChange(previousClassification);
          window.location.reload();
        }
        break;
      }
      case 'newsletterSubscribe': {
        let pianoUid: string | null;
        let zeroBounceValidated = false;
        let zeroBounceStatus = '';
        let zeroBounceSubStatus = '';

        // Check for logged in user
        const loggedInUser = tp.pianoId.getUser();
        // Compare logged in user email to subscription email
        const isCurrentUser = loggedInUser?.email === email;

        // If the user is logged in and the emails match, use that pianoUid
        if (isCurrentUser && loggedInUser) {
          pianoUid = loggedInUser.uid;
        } else {
          pianoUid = await getExistingPianoUid(email);
        }

        if (pianoUid) {
          // Get zerobounce values
          const validateEmailResponse = await fetch('/api/validateEmail', {
            body: JSON.stringify({ email }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
          const validateEmailResponseData = await validateEmailResponse.json();
          if (validateEmailResponseData.success) {
            zeroBounceValidated = true;
            zeroBounceStatus = validateEmailResponseData.status || '';
            zeroBounceSubStatus = validateEmailResponseData.subStatus || '';
          } else {
            iframe.contentWindow?.postMessage(
              {
                event: 'error message newsletter subscribe',
                info: validateEmailResponseData.message,
              },
              iframeOrigin,
            );
            return;
          }
        } else if (!pianoUid) {
          // If no pianoUid found, register a new passwordless account
          const registerData = await registerPasswordlessPianoUser({ consent, email });
          // Register first checks for ZeroBounce errors. If there are ZeroBounce errors, return the error message to the iframe
          if (registerData.success === false) {
            if (registerData.isZeroBounceError) {
              iframe.contentWindow?.postMessage(
                {
                  event: 'error message newsletter subscribe',
                  info: registerData.message,
                },
                iframeOrigin,
              );
              return;
            }
          }
          // We continue executing the rest of the code even if there are errors with registering the user.
          // If success, we set the pianoUid and the ZeroBounce status and subStatus.
          if (registerData.success) {
            pianoUid = registerData.uid;
            zeroBounceValidated = true;
            zeroBounceStatus = registerData.status || '';
            zeroBounceSubStatus = registerData.subStatus || '';
          }
        }

        // Subscribe the existing or newly created user to the newsletter
        const newsletterSubscribeResponse = await fetch('/api/subscribeUserNewsletter', {
          body: JSON.stringify({
            bypassReCaptcha: true,
            campaign: utmCampaign,
            country,
            email,
            formName: [formName],
            medium: utmMedium,
            pagePath: window.location.pathname,
            pianoUid: pianoUid ? pianoUid : undefined,
            reCaptchaToken: '',
            signupCampaign: templateId,
            signupConsent: consent,
            signupMedium: displayMode,
            signupSource: 'piano',
            source: utmSource,
            zeroBounceStatus,
            zeroBounceSubStatus,
            zeroBounceValidated,
          }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });

        if (newsletterSubscribeResponse.ok) {
          // remove minimum height and margin for the reserved space
          const homepageContainer = document.getElementById('hp-piano-template-plea-container')?.parentElement;
          const articleContainer = document.getElementById('article-piano-template-plea-container')?.parentElement;
          const container = homepageContainer || articleContainer;
          if (container) {
            // Animate height and margin down to 0
            container.style.transition = 'min-height 1s ease';
            container.style.minHeight = '0px';
          }

          const isArticle = Boolean(articleContainer);

          if (isArticle) {
            trackOptimizelyEvent(optimizely, 'ceo_daily_subscription_success', {
              value: OPTIMIZELY_EVENT_VALUES.newsletterSubscription,
            });
          }

          iframe.contentWindow?.postMessage(
            {
              event: 'success message newsletter subscribe',
              info: 'Thank you for subscribing.',
            },
            iframeOrigin,
          );
        } else {
          iframe.contentWindow?.postMessage(
            {
              event: 'error message newsletter subscribe',
              info: 'Your subscription could not be registered.',
            },
            iframeOrigin,
          );
        }
        break;
      }
      case 'subscriptionResume': {
        try {
          const resumeResponse = await fetch('/api/resumeSubscription', {
            method: 'POST',
          });
          if (resumeResponse.ok) {
            // Send success message to iframe on successful resume, then reload the page after a short delay
            iframe.contentWindow?.postMessage(
              {
                event: 'success message subscription resume',
                info: 'Your subscription has been successfully resumed.',
              },
              iframeOrigin,
            );
            setTimeout(() => {
              window.location.reload();
            }, SUBSCRIPTION_RESUME_RELOAD_DELAY_MS);
          } else {
            // Send error message to iframe on failure
            const resumeResponseData = await resumeResponse.json();
            iframe.contentWindow?.postMessage(
              {
                event: 'error message subscription resume',
                info: resumeResponseData.message || 'Your subscription could not be resumed.',
              },
              iframeOrigin,
            );
          }
        } catch {
          // Send message to iframe on unexpected error
          iframe.contentWindow?.postMessage(
            {
              event: 'error message subscription resume',
              info: 'Your subscription could not be resumed.',
            },
            iframeOrigin,
          );
        }
        break;
      }
      default:
        break;
    }
  },
  loginSuccess: async (loginData: LoginDataProps) => {
    const { uid: pianoUID } = tp.pianoId.getUser();
    const trackLogin = async () => {
      try {
        const passwordlessSignInInitiated = getSessionStorageObject('__pwlessSI');
        if (passwordlessSignInInitiated?.initiated) {
          sendEventOnConsent(
            {
              deviceType: getDeviceForTracking(),
              eventAction: 'login_attempt',
              loginMethod: 'Piano',
              loginSuccess: true,
              loginType: 'passwordless',
              pianoUserId: loginData.params.sub,
            },
            'signInEventTracker',
          );
          removeSessionStorageItem('__pwlessSI');
        }

        trackOptimizelyEvent(optimizely, 'user_signed_in', {});

        const trackingData = {
          eventAction: '',
          eventLabel: '',
        };

        if (loginData.registration) {
          // I don't believe this needs that loginData.source check anymore, but for safety leaving that check in
          // If not, we can remove the paywall as part of the RegistrationSource string literal
          await geoIp(pianoUID, loginData.source === 'OFFER' ? 'paywall' : 'sign_in_popup');
          trackingData.eventAction = 'create account';
          trackingData.eventLabel = 'account creation successful';
        } else {
          trackingData.eventAction = 'sign in';
          trackingData.eventLabel = 'sign in successful';
        }

        sendEventOnConsent({
          ...trackingData,
          eventCategory: 'account',
        });
      } catch (_error) {
        // console.error(error);
      }
    };

    await trackLogin();
    const previousClassification = getTrackedUserClassification();
    const conversionType = loginData.registration ? 'registration' : 'login';

    trackCurrentUserConversion(conversionType, previousClassification);
    trackCurrentUserStateChange(previousClassification);

    if (typeof localStorage !== 'undefined' && localStorage.getItem('pendingSaveArticleId')) {
      localStorage.setItem('pendingSaveAuthMethod', loginData.registration ? 'registration' : 'login');
    }

    const checkoutRedirect = CookieService.get('__rdCO');
    const signInRedirect = CookieService.get('__rdSI');

    if (checkoutRedirect) {
      CookieService.remove('__rdCO');
      updateLocalStorageObject('checkoutTrackingProps', {
        login_method: 'piano',
        login_type: 'passwordless',
      });
      window.location.href = decodeURIComponent(checkoutRedirect);
    } else if (signInRedirect) {
      CookieService.remove('__rdSI');
      window.location.href = signInRedirect;
    } else if (loginData.source !== 'OFFER') {
      window.location.reload();
    } else {
      reloadOnClose = true;
    }
  },
  logout: () => {
    updateLocalStorageObject('checkoutTrackingProps', {
      login_method: null,
      login_type: null,
    });
    const domain = window.location.hostname;
    CookieService.remove('uatr');
    CookieService.remove('uat');
    CookieService.remove('__utp', { domain });
    CookieService.remove('pianoUid');
    CookieService.remove('pianoPaused');
    CookieService.remove('pianoAccess');
    sessionStorage.removeItem('otSessionUser');
    trackCurrentUserStateChange();
    window.location.reload();
  },
  manualCreditRedeemed: () => {
    tp.template.close();
    window.location.reload();
  },
  registrationSuccess: () => {
    const previousClassification = getTrackedUserClassification();

    trackCurrentUserConversion('registration', previousClassification);
    trackCurrentUserStateChange(previousClassification);
  },
  setResponseVariable: ({ responseVariables }: ResponseVariableEvent) => {
    updateResponseVariables(responseVariables);
  },
  showOffer: async (offerParams: OfferParams) => {
    const { containerSelector, offerId, displayMode, experienceActionId, experienceId, templateId, templateVariantId } =
      offerParams;
    if (containerSelector === '.paywallax-ii-selector') {
      updateResponseVariables({ showPaywallax: true });
    }

    try {
      const response = await fetch(`/api/getPianoData/offerTerms/${offerId}/`);
      let terms: OfferTerms;
      let listOfTerms: string[] = [];
      if (response.ok) {
        terms = (await response.json()) as OfferTerms;
        sessionData.offerTerms = terms;
        listOfTerms = Object.values(terms).map((term: Term) => term.name);
      }

      const lts = getLts(tp, experienceId, 'showOffer');

      productPayload = {
        displayMode,
        experienceActionId,
        experienceId,
        lts: lts === 'no_score' ? lts : lts?.split(':')[2],
        offerId,
        templateId,
        templateVariantId,
        terms: listOfTerms,
      };

      const subscribePage = isSubscribePage();

      if (subscribePage) {
        const currentCheckoutProps = getLocalStorageObject('checkoutTrackingProps');
        // We only want to overwrite exposure props on subscribe pages if there aren't values from a recent,
        // more relevant exposure
        if (!currentCheckoutProps?.persistExposureProps) {
          updateLocalStorageObject('checkoutTrackingProps', {
            experience_action_id: experienceActionId,
            experience_id: experienceId,
            lts: lts === 'no_score' ? lts : lts?.split(':')[2],
            offer_id: offerId,
            template_id: templateId,
            template_variant_id: templateVariantId,
            terms: listOfTerms,
          });
        }
        // We only want to overwrite referral params on subscribe pages if there aren't values from a subscribe page
        // exposure that resulted from navigating from another template (which would have set relevant params)
        // persistReferralParams is set to true when clicking "change subscription" from checkout, and also when the referral params
        // are initially set from a non-subscribe page offer template exposure
        if (!currentCheckoutProps?.persistReferralParams) {
          const queryString = window.location.search;
          updateLocalStorageObject('checkoutTrackingProps', {
            checkout_referral_params: queryString || null,
            persistReferralParams: true,
          });
        }
      } else {
        // For non-subscribe pages, we always want to set the exposure props from the most recent offer template shown
        updateLocalStorageObject('checkoutTrackingProps', {
          experience_action_id: experienceActionId,
          experience_id: experienceId,
          lts: lts === 'no_score' ? lts : lts?.split(':')[2],
          offer_id: offerId,
          persistExposureProps: true,
          template_id: templateId,
          template_variant_id: templateVariantId,
          terms: listOfTerms,
        });
      }

      const productViewedPayload = {
        ...productPayload,
        logged_in: Boolean(tp.pianoId.getUser()),
        products: [
          {
            offer_id: offerId,
          },
        ],
        timestamp: Date.now(),
      };
      sendEventOnConsent({
        eventAction: 'product viewed',
        eventCategory: 'paywall',
        eventLabel: 'offer shown to user',
        ...productViewedPayload,
      });
      // Track paywall exposure to Optimizely
      trackOptimizelyEvent(optimizely, 'paywall_exposures', {});
    } catch {
      // Intentionally ignore errors during offer display and tracking
    }
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((i) => {
      if (i.name.includes('offer')) {
        i.setAttribute('aria-label', 'subscribe offer');
      }
    });
  },
  showTemplate: (templateParams: TemplateParams) => {
    const { containerSelector, templateId, templateVariantId, experienceId, experienceActionId, displayMode } =
      templateParams;
    const lts = getLts(tp, experienceId, 'showTemplate');
    // We want to use the exposure props from the most recent showTemplate exposure
    updateLocalStorageObject('checkoutTrackingProps', {
      experience_action_id: experienceActionId,
      experience_id: experienceId,
      lts: lts === 'no_score' ? lts : lts?.split(':')[2],
      persistExposureProps: true,
      template_id: templateId,
      template_variant_id: templateVariantId,
    });

    const productViewedPayload = {
      displayMode,
      eventAction: 'piano template viewed',
      eventLabel: 'offer shown to user',
      experienceActionId,
      experienceId,
      templateId,
      templateVariantId,
    };

    if (displayMode === 'inline') {
      const container = document.querySelector(containerSelector);
      if (containerSelector && container) {
        switch (true) {
          case containerSelector in PROMO_CONTAINER_OPTIMIZELY_EVENTS:
            buildTemplateIntersectionObserver({
              container,
              optimizely,
              optimizelyEvent: PROMO_CONTAINER_OPTIMIZELY_EVENTS[containerSelector],
              sendEventOnConsent,
              trackingPayload: {
                ...productViewedPayload,
                eventCategory: 'promotion',
              },
            });
            break;
          case containerSelector === '#hp-piano-template-plea-container':
          case containerSelector === '#article-piano-template-plea-container':
            buildTemplateIntersectionObserver({
              container,
              sendEventOnConsent,
              trackingPayload: {
                ...productViewedPayload,
                eventCategory: 'newsletter',
              },
            });
            break;
          default:
            buildTemplateIntersectionObserver({
              container,
              optimizely,
              optimizelyEvent: 'paywall_exposures',
              sendEventOnConsent,
              trackingPayload: {
                ...productViewedPayload,
                eventCategory: 'paywall',
              },
            });
        }
      }
    } else {
      // If displayMode is modal, then modals are always above the fold, so we can send the viewed event immediately
      sendEventOnConsent({
        eventCategory: 'paywall',
        ...productViewedPayload,
      });
      // Track paywall exposure to Optimizely
      trackOptimizelyEvent(optimizely, 'paywall_exposures', {});
    }

    if (containerSelector === '.paywallax-ii-selector') {
      updateResponseVariables({ showPaywallax: true });
    }
    if (displayMode === 'modal') {
      // Modal template close buttons are technically on the outside of the iframe, so to track close clicks, we need to add a listener here
      window.addEventListener('click', (event) => {
        const eventTarget = event.target as HTMLElement;
        const eventTargetParent = eventTarget.parentElement as HTMLElement;
        if (
          event.target &&
          eventTarget.className.includes('tp-close') &&
          eventTargetParent.className.includes('tp-iframe-wrapper')
        ) {
          const templateTrackingPayload = {
            displayMode,
            eventAction: 'newsletter template clicked',
            eventCategory: 'piano template',
            eventLabel: 'user clicked on newsletter template',
            manualClickText: (event.target as HTMLElement).textContent,
            manualClickType: 'close',
            manualContentPlacement: 'piano template',
          };
          sendEventOnConsent(templateTrackingPayload);
        }
      });
    }
  },
});
